import { Bot as BotIcon } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import IconBot from "@/assets/icons/bot.svg?react";
import IconCompare from "@/assets/icons/compare.svg?react";
import IconDashboard from "@/assets/icons/dashboard.svg?react";
import IconDept from "@/assets/icons/dept.svg?react";
import IconEditSquare from "@/assets/icons/editSquare.svg?react";
import IconOpen from "@/assets/icons/open.svg?react";
import IconSearch from "@/assets/icons/search.svg?react";
import IconSettings from "@/assets/icons/settings.svg?react";
import IconStats from "@/assets/icons/stats.svg?react";
import { ThreadList } from "@/components/agent/ThreadList";
import CategorySelectModal from "@/components/ui/CategorySelectModal";
import { ChatHistory } from "@/components/ui/ChatHistory";
import DisplayGroupSelectCard from "@/components/ui/DisplayGroupSelectCard";
import { routes } from "@/configs/router.config/routes";
import { AppContext } from "@/contexts/AppContext";
import { useAnalyses } from "@/hooks/useAnalyses";
import useAuth from "@/hooks/useAuth";
import useConfig from "@/hooks/useConfig";
import { useConversation } from "@/hooks/useConversation";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { TMP_CONVERSATION_KEY } from "@/models/conversation";
import { useAppStore } from "@/stores/ui/useAppStore";
import { useDeepAnalysisStore } from "@/stores/ui/useDeepAnalysisStore";

interface Props {
  className?: string;
}

export const Sidebar: React.FC<Props> = ({
  className = "",
}) => {
  const [isOpen, setIsSideBarOpen] = useLocalStorage("isSideBarOpen", true);
  const isAgentMode = useAppStore(s => s.isAgentMode);
  const [showSectionTitles, setShowSectionTitles] = useState(true);
  const toggleSidebar = () => {
    setIsSideBarOpen(!isOpen);
  };

  // アニメーション中の状態を管理
  useEffect(() => {
    // アニメーション開始: すぐに非表示
    setShowSectionTitles(false);

    // アニメーション終了後: ゆっくり表示
    const timer = setTimeout(() => {
      setShowSectionTitles(true);
    }, 200); // transition-all duration-200 と同じ時間

    return () => clearTimeout(timer);
  }, [isOpen]);

  const { t } = useTranslation("voiceek_ui");
  const { t: tCommon } = useTranslation("common");
  const { isComparisionMode, setIsComparisionMode } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const { isAdmin } = useAuth();
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { latestAnalysis } = useAnalyses();
  const { displayGroups } = useDisplayGroups();
  const [isOpenDisplayGroupSelect, setIsOpenDisplayGroupSelect] = useState(false);
  const [isOpenCategorySelect, setIsOpenCategorySelect] = useState(false);
  const {
    setCurrentConversationId,
  } = useConversation();

  const openChatModal = useDeepAnalysisStore((s) => s.openChatModal);
  const openVocSearchModal = useDeepAnalysisStore((s) => s.openVocSearchModal);

  const isAnalysisPage = location.pathname.startsWith(routes.analysis(sessionCurrentWorkspaceId)(""));

  const itemClasses = cn(
    isOpen ? "w-full" : "w-10",
    "h-10 pl-2 flex items-center justify-start rounded-[10px] cursor-pointer text-white fill-white transition-colors",
    "hover:bg-[#5D77BE] hover:rounded-[10px]",
  );
  const iconClasses = "min-w-6 fill-inherit";
  const sectionTitleClasses = cn(
    "font-bold text-[#A9B5D6] text-nowrap text-center w-full flex justify-center",
    !isOpen && "text-xs whitespace-normal break-all",
    showSectionTitles ? "opacity-100 transition-opacity duration-500" : "opacity-0 transition-none",
  );
  const sectionTitleWrapperClasses = cn(
    "flex flex-col justify-end h-8 transition-all duration-200",
    !isOpen && "w-full",
  );
  const sectionWrapperClasses = cn(
    "flex flex-col min-h-0 w-full mb-5 items-start gap-2 justify-start transition-all duration-200",
  );
  const itemTextWrapperClasses = cn(
    "ml-2 font-bold min-w-[120px] text-left overflow-hidden",
    !isOpen && "invisible",
  );
  const itemTextClasses = cn("block transition-all opacity-0", isOpen && "opacity-100");

  return (
    <div className={cn(isOpen ? "w-[260px]" : "w-[60px]", "flex transition-all duration-200", className)}>
      <div className="flex flex-col w-full h-full items-start p-2.5 bg-[#4c65aa]">
        {/* < 閉じる */}
        <button
          className={cn(
            "h-10 flex items-center cursor-pointer",
            isOpen ? "w-full justify-end" : "w-10 justify-start",
          )}
          onClick={toggleSidebar}
        >
          <IconOpen className={cn("min-w-10", isOpen && "scale-x-[-1]")} />
          <div className={cn("w-15 text-white font-bold overflow-hidden")}>
            <span
              className={cn(
                "block transition-all duration-200 opacity-0 -translate-x-[200%]",
                isOpen && "overflow-visible opacity-100 translate-x-0",
              )}
            >
              {tCommon("button.close")}
            </span>
          </div>
        </button>
        {isAgentMode
          ? <ThreadList />
          : (
            <>
              <div className={sectionWrapperClasses}>
                <div className={sectionTitleWrapperClasses}>
                  <span className={sectionTitleClasses}>{t("sidebar.analysis")}</span>
                </div>

                {/* 部署 */}
                <button
                  className="w-full h-10"
                  onClick={() => setIsOpenDisplayGroupSelect(!isOpenDisplayGroupSelect)}
                >
                  <div
                    className={cn(itemClasses, isOpenDisplayGroupSelect && "bg-white text-[#5F6368] fill-[#5F6368]")}
                  >
                    <IconDept className={iconClasses} />
                    <div className={itemTextWrapperClasses}>
                      <span className={itemTextClasses}>
                        {config?.displayGroupLabel}
                      </span>
                    </div>
                  </div>
                </button>

                {/* 分析 */}
                <button className={itemClasses} onClick={() => setIsOpenCategorySelect(true)}>
                  <IconStats className={iconClasses} />
                  <div className={itemTextWrapperClasses}>
                    <span className={itemTextClasses}>{t("sidebar.analysisTarget")}</span>
                  </div>
                </button>

                {/* 比較モード */}
                {isAnalysisPage && (
                  <button
                    className={cn(
                      itemClasses,
                      "pl-[11px]",
                      isComparisionMode && "bg-white text-[#5F6368] fill-[#5F6368]",
                    )}
                    onClick={() => setIsComparisionMode(!isComparisionMode)}
                  >
                    <IconCompare className={cn(iconClasses, "min-w-[18px]")} />
                    <div className={cn(itemTextWrapperClasses, "ml-[11px]")}>
                      <span className={itemTextClasses}>{t("sidebar.comparisonMode")}</span>
                    </div>
                  </button>
                )}

                {/* ダッシュボード */}
                {config?.enableBi && (
                  <button
                    className={itemClasses}
                    onClick={() =>
                      location.pathname === routes.dashboard
                        ? globalThis.location.reload()
                        : navigate("/dashboard")}
                  >
                    <IconDashboard className={iconClasses} />
                    <div className={itemTextWrapperClasses}>
                      <span className={itemTextClasses}>{t("sidebar.dashboard")}</span>
                    </div>
                  </button>
                )}
              </div>

              <div className={cn(sectionWrapperClasses, "flex-1")}>
                <div className={sectionTitleWrapperClasses}>
                  <span className={sectionTitleClasses}>{t("sidebar.deepAnalysis")}</span>
                </div>

                {/* エージェント */}
                {config?.enableAgent && (
                  <Link to={routes.agent(sessionCurrentWorkspaceId)} className="w-full">
                    <div className={itemClasses}>
                      <BotIcon className="min-w-6" />
                      <div className={itemTextWrapperClasses}>
                        <span className={itemTextClasses}>エージェント</span>
                      </div>
                    </div>
                  </Link>
                )}

                {/* VOC検索 */}
                <button className={itemClasses} onClick={openVocSearchModal}>
                  <IconSearch className={iconClasses} />
                  <div className={itemTextWrapperClasses}>
                    <span className={itemTextClasses}>{t("sidebar.vocSearch")}</span>
                  </div>
                </button>

                {/* チャットボット */}
                <button
                  className={itemClasses}
                  onClick={() => {
                    openChatModal();
                  }}
                >
                  <IconBot className={iconClasses} />
                  <div className={itemTextWrapperClasses}>
                    <span className={itemTextClasses}>{t("sidebar.chatbot")}</span>
                  </div>
                </button>
                {isOpen && (
                  <>
                    <button
                      className="ml-10 h-10 w-[200px] flex gap-1 items-center justify-start rounded-[10px] cursor-pointer text-white fill-white transition-colors hover:bg-[#5D77BE]"
                      onClick={() => {
                        setCurrentConversationId(TMP_CONVERSATION_KEY);
                        openChatModal();
                      }}
                    >
                      <IconEditSquare className={iconClasses} />
                      <div className="font-bold min-w-[120px] text-left overflow-hidden">
                        <span className={itemTextClasses}>{t("chat.newChat")}</span>
                      </div>
                    </button>
                    <ChatHistory />
                  </>
                )}
              </div>

              {/* セッティング */}
              {isAdmin && (
                <Link to={routes.admin} className="block bg-[#4c65aa] ">
                  <button className={itemClasses}>
                    <IconSettings className="min-w-6 fill-inherit mr-2 " />
                  </button>
                </Link>
              )}
            </>
          )}
      </div>
      {latestAnalysis && displayGroups && (
        <CategorySelectModal
          analysis={latestAnalysis}
          displayGroups={displayGroups}
          isOpenModal={isOpenCategorySelect}
          setIsOpenModal={setIsOpenCategorySelect}
          submit={(displayGroupId: string, categoryPath: string) => {
            navigate(
              `${
                routes.analysis(sessionCurrentWorkspaceId)(displayGroupId)
              }?analysis=${latestAnalysis.id}&category=${categoryPath}`,
            );
          }}
        />
      )}

      {displayGroups && (
        <DisplayGroupSelectCard
          displayGroups={displayGroups}
          isOpenDisplayGroupSelect={isOpenDisplayGroupSelect}
          setIsOpenDisplayGroupSelect={setIsOpenDisplayGroupSelect}
          className={cn("fixed top-[100px] translate-0", isOpen ? "left-[260px]" : "left-[60px]")}
        />
      )}
    </div>
  );
};
